import { AlertTriangle, Loader2 } from "lucide-react";
import type { PlatformConnection } from "../models/Platform";
import { PlatformConnectionIcon } from "./PlatformConnectionIcon";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";

export interface ConnectionScheduleImpact {
  scheduledPostCount: number;
  scheduledTaskCount: number;
  deferredPostCount: number;
}

interface DisconnectAccountDialogProps {
  connection: PlatformConnection | null;
  open: boolean;
  impact: ConnectionScheduleImpact | null;
  isImpactLoading: boolean;
  impactError: string | null;
  isDisconnecting: boolean;
  disconnectError: string | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

function pluralizePosts(count: number): string {
  return `${count} scheduled post${count === 1 ? "" : "s"}`;
}

const PLATFORM_NAMES: Record<PlatformConnection["platform"], string> = {
  instagram: "Instagram",
  youtube: "YouTube",
  tiktok: "TikTok",
  linkedin: "LinkedIn",
  facebook: "Facebook",
  pinterest: "Pinterest",
};

export function DisconnectAccountDialog({
  connection,
  open,
  impact,
  isImpactLoading,
  impactError,
  isDisconnecting,
  disconnectError,
  onOpenChange,
  onConfirm,
}: DisconnectAccountDialogProps) {
  const scheduledPostCount = impact?.scheduledPostCount ?? 0;
  const hasScheduledPosts = scheduledPostCount > 0;

  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!isDisconnecting) onOpenChange(nextOpen);
      }}
    >
      <AlertDialogContent className="max-w-md gap-6">
        {connection && (
          <>
            <AlertDialogHeader className="gap-3 space-y-0 text-left">
              <div className="flex items-center gap-3">
                <PlatformConnectionIcon
                  platform={connection.platform}
                  profileImage={connection.profileImage}
                  profileName={connection.profileName}
                  size="md"
                  disableTooltip
                />
                <div className="min-w-0">
                  <AlertDialogTitle className="truncate">
                    Disconnect {connection.profileName}?
                  </AlertDialogTitle>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    {PLATFORM_NAMES[connection.platform]} account
                  </p>
                </div>
              </div>
              <AlertDialogDescription className="max-w-[58ch] leading-6">
                This account won&apos;t be available for new posts. Published
                posts will remain in your history.
              </AlertDialogDescription>
            </AlertDialogHeader>

            <div className="grid gap-3">
              {isImpactLoading ? (
                <div
                  className="flex min-h-20 items-center gap-3 rounded-lg border bg-muted/35 px-4 py-3 text-sm text-muted-foreground"
                  role="status"
                >
                  <Loader2
                    className="h-4 w-4 shrink-0 animate-spin"
                    aria-hidden
                  />
                  Checking scheduled posts…
                </div>
              ) : impactError ? (
                <div
                  className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-950"
                >
                  <div className="flex gap-3" role="status">
                    <AlertTriangle
                      className="mt-0.5 h-4 w-4 shrink-0 text-amber-700"
                      aria-hidden
                    />
                    <div className="grid gap-1 text-sm">
                      <p className="font-medium">
                        We couldn&apos;t check post schedules
                      </p>
                      <p className="leading-5 text-amber-900">{impactError}</p>
                      <p className="leading-5 text-amber-900">
                        We&apos;ll check again when you disconnect and remove this
                        account from any affected schedules first. If that check
                        fails, the account will stay connected.
                      </p>
                    </div>
                  </div>
                </div>
              ) : hasScheduledPosts ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-950">
                  <div className="flex gap-3">
                    <AlertTriangle
                      className="mt-0.5 h-4 w-4 shrink-0 text-amber-700"
                      aria-hidden
                    />
                    <div className="grid gap-1 text-sm">
                      <p className="font-medium">
                        {pluralizePosts(scheduledPostCount)}{" "}
                        {scheduledPostCount === 1 ? "is" : "are"} set to
                        publish to this account
                      </p>
                      <p className="leading-5 text-amber-900">
                        Disconnecting will remove this account from those
                        schedules. Posts scheduled to other accounts will keep
                        their publishing times.
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}

              {disconnectError && (
                <p
                  className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm leading-5 text-destructive"
                  role="alert"
                >
                  {disconnectError}
                </p>
              )}
            </div>

            <AlertDialogFooter className="gap-2 sm:space-x-0">
              <AlertDialogCancel disabled={isDisconnecting}>
                Keep account
              </AlertDialogCancel>
              <Button
                type="button"
                variant="destructive"
                onClick={onConfirm}
                disabled={isImpactLoading || isDisconnecting}
              >
                {isDisconnecting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                )}
                {isDisconnecting
                  ? "Disconnecting…"
                  : hasScheduledPosts
                    ? `Disconnect and update ${scheduledPostCount} post${
                        scheduledPostCount === 1 ? "" : "s"
                      }`
                    : "Disconnect account"}
              </Button>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
